import { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'states/hooks'
import LogoImgLight from 'assets/images/svg/crewworks-slim-light.svg'
import LogoImgDark from 'assets/images/svg/crewworks-slim-dark.svg'
import { useTranslation } from '@crew/modules/i18n'
import UserGuidePngLight from 'assets/images/png/userguide-light.png'
import UserGuidePngDark from 'assets/images/png/userguide-dark.png'
import LearnMorePng from 'assets/images/png/learn-more.png'
import ArrowCircleDownOutlineRounded from '~icons/material-symbols/arrow-circle-down-outline-rounded'
import { MediaCard } from './components/mediaCard/mediaCard'
import { CrewLink } from 'components/elements/crewLink/crewLink'
import IconInfo from '~icons/material-symbols/info-outline'
import ArrowUpwardAltRounded from '~icons/material-symbols/arrow-upward-alt-rounded'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { useInsertOrReplaceSettingMutation } from '@crew/apis/setting/settingApis'
import { useEffectOnce, useMount, useUnmount } from '@dx-system/react-use'
import { InsertOrReplaceSettingRequest } from '@crew/apis/setting/models/insertOrReplaceSetting/request'
import { SettingKeyType } from '@crew/enums/app'
import { useLoadUserSettings } from '@crew/states'
import classNames from 'classnames'
import {
  closeRightSideBar,
  openRightSideBar,
} from 'features/app/states/appSlice'
import { useShowApiErrors } from 'hooks/useShowApiErrors'

// ユーザーガイドのバージョン（既読済みかどうかのバッジ判定に使用する）
// NOTICE: ユーザーガイドを更新する場合、このバージョンも更新すること
export const USER_GUIDE_VERSION = 6

// サポートサイトの「各機能の基本的な使い方」のURL
const SUPPORT_SITE_HOWTO_URL =
  'https://support.crewworks.net/support/solutions/150000213591'

// おすすめ活用動画
const RecommendationVideoUrl = {
  description:
    'label.userGuide.videoDescription.howToUse.taskManagementOptions',
  url: 'https://www.youtube.com/embed/K8uNd5qirRo?si=137Rp3rwxKMPeskI',
}

// 「使い方（基本編）」の動画リスト
const HowToUsageVideoUrls = [
  // ユーザー招待
  {
    id: 'user-guide-video-how-to-use-invite-user',
    description: 'label.userGuide.videoDescription.howToUse.inviteUser',
    url: 'https://www.youtube.com/embed/zq45BkdNyRA?si=S9ATFVsg5sCsQmWa',
  },
  // プロジェクト作成
  {
    id: 'user-guide-video-how-to-use-create-project',
    description: 'label.userGuide.videoDescription.howToUse.createProject',
    url: 'https://www.youtube.com/embed/KC6hhFzZvPo?si=lzm_GbcZchrEAO9o',
  },
  // チャット
  {
    id: 'user-guide-video-how-to-use-chat',
    description: 'label.userGuide.videoDescription.howToUse.chat',
    url: 'https://www.youtube.com/embed/gGP5aPCZXNE?si=i_zMYugZYMJyR_En',
  },
  // Web会議
  {
    id: 'user-guide-video-how-to-use-web-meeting',
    description: 'label.userGuide.videoDescription.howToUse.webMeeting',
    url: 'https://www.youtube.com/embed/2wnTTt43I7A?si=HuOuyhgXQ3RtJCz6',
  },
  // タスク管理
  {
    id: 'user-guide-video-how-to-use-task-management',
    description: 'label.userGuide.videoDescription.howToUse.taskManagement',
    url: 'https://www.youtube.com/embed/5Yg-P1NoMQE?si=U2XGeGfno-eVOT-2',
  },
  // プロジェクト管理
  {
    id: 'user-guide-video-how-to-use-project-management',
    description: 'label.userGuide.videoDescription.howToUse.projectManagement',
    url: 'https://www.youtube.com/embed/WFYkkpqYZv0?si=eNjzRjptNSLpNT4-',
  },
  // ファイル共有
  {
    id: 'user-guide-video-how-to-use-file-sharing',
    description: 'label.userGuide.videoDescription.howToUse.fileSharing',
    url: 'https://www.youtube.com/embed/NwIXjduvafI?si=6xL5D-_6FnmQbTx5',
  },
  // 横断検索
  {
    id: 'user-guide-video-how-to-use-cross-search',
    description: 'label.userGuide.videoDescription.howToUse.crossSearch',
    url: 'https://www.youtube.com/embed/zETLhNuu4Pg?si=b0ANt_jMVJcMaeXg',
  },
  // AIアシスタント
  {
    id: 'user-guide-video-how-to-use-ai-assistant',
    description: 'label.userGuide.videoDescription.howToUse.aiAssistant',
    url: 'https://www.youtube.com/embed/fF21sF09R2s?si=IU_wtIhr10AmGP60',
  },
  // AI議事録
  {
    id: 'user-guide-video-how-to-use-ai-meeting-minutes',
    description: 'label.userGuide.videoDescription.howToUse.aiMeetingMinutes',
    url: 'https://www.youtube.com/embed/HlE3YuQjvi0?si=QSJMi2kaK7hRKRhS',
  },
  // タスク管理　オプション機能
  {
    id: 'user-guide-video-how-to-use-task-management-options',
    description:
      'label.userGuide.videoDescription.howToUse.taskManagementOptions',
    url: 'https://www.youtube.com/embed/K8uNd5qirRo?si=137Rp3rwxKMPeskI',
  },
]

// 「活用法（応用編）」の動画リスト
const UsageTipsVideoUrls = [
  // 説明しなくても伝わる！CrewWorksでストレスゼロの業務引継ぎ術
  {
    id: 'user-guide-video-usage-tips-business-handover',
    description: 'label.userGuide.videoDescription.usageTips.businessHandover',
    url: 'https://www.youtube.com/embed/0wDfpXsxrao?si=xzxZZfOh8Id7_WQy',
  },
  // 会議の決定、59%が放置！？情報集約＆ナレッジ化で差をつける
  {
    id: 'user-guide-video-usage-tips-knowledge-management',
    description:
      'label.userGuide.videoDescription.usageTips.knowledgeManagement',
    url: 'https://www.youtube.com/embed/2B1FLiPKhY0?si=KntMDvoxdGcwwpU1',
  },
  // 超絶工数削減！オールインワンツールだからできるお手軽資料レビュー
  {
    id: 'user-guide-video-usage-tips-document-review',
    description: 'label.userGuide.videoDescription.usageTips.documentReview',
    url: 'https://www.youtube.com/embed/KUgqm3BgoeU?si=_gZGJkdxxf6odBBc',
  },
  // 情報が埋もれない！見落とさない！情報の混在を防ぐCrewWorksのチャット機能
  {
    id: 'user-guide-video-usage-tips-chat',
    description: 'label.userGuide.videoDescription.usageTips.chat',
    url: 'https://www.youtube.com/embed/QpGfqmkwIGA?si=CZ7qvekYHidDvZfl',
  },
  // もうExcelには戻れない！上司も部下も助かる、CrewWorksで変わるタスク管理
  {
    id: 'user-guide-video-usage-tips-task-management',
    description: 'label.userGuide.videoDescription.usageTips.taskManagement',
    url: 'https://www.youtube.com/embed/RSohX8FEdKw?si=vw5WBA3xxaQoRH5I',
  },
  // 使うだけでナレッジになる？AIがナレッジを活かす時代へ
  {
    id: 'user-guide-video-usage-tips-ai-assistant',
    description: 'label.userGuide.videoDescription.usageTips.aiAssistant',
    url: 'https://www.youtube.com/embed/kiaFRHDutwI?si=-UfTp9rMVVdxGWg3',
  },
  // Web会議やチャットだけじゃない！CrewWorksで外部ユーザーと進捗状況まで共有
  {
    id: 'user-guide-video-usage-tips-external-user-collaboration',
    description:
      'label.userGuide.videoDescription.usageTips.externalUserCollaboration',
    url: 'https://www.youtube.com/embed/aMcZShutZUU?si=d2d-aBX4HvEuE_j7',
  },
  // こんな使い方は損！ついついやりがちなCrewWorksで気をつけたい使い方6選
  {
    id: 'user-guide-video-usage-tips-usage-tips6',
    description: 'label.userGuide.videoDescription.usageTips.usageTips6',
    url: 'https://www.youtube.com/embed/0fLbwKg4uAc?si=qXYmR-mf7qI-2nL7',
  },
  // 探すより、聞く。考えるより、相談する。社内専用AIで仕事革命
  {
    id: 'user-guide-video-usage-tips-internal-ai-assistant',
    description:
      'label.userGuide.videoDescription.usageTips.internalAiAssistant',
    url: 'https://www.youtube.com/embed/LNxxml85U_Y?si=bgJoMx6YTh5FecmO',
  },
  // ただの保管から活用へ。関連ファイルの一元管理とAI要約
  {
    id: 'user-guide-video-usage-tips-file-hub-with-ai-summary',
    description:
      'label.userGuide.videoDescription.usageTips.fileHubWithAiSummary',
    url: 'https://www.youtube.com/embed/HoHSI46lJmw?si=7naKQmHm5BKATidK',
  },
  // タスクがごちゃごちゃ…それ”親子関係”で一発解決できます！
  {
    id: 'user-guide-video-usage-tips-task-hierarchy',
    description: 'label.userGuide.videoDescription.usageTips.taskHierarchy',
    url: 'https://www.youtube.com/embed/zG-4FpS567k?si=W7XS1XY60f89GqNO',
  },
  // 対面・録画・オンライン…全部OK！AIが自動で議事録を作成
  {
    id: 'user-guide-video-usage-tips-ai-meeting-minutes',
    description: 'label.userGuide.videoDescription.usageTips.aiMeetingMinutes',
    url: 'https://www.youtube.com/embed/nqiyxYivFuA?si=ktlBZc1NXuS2u4nV',
  },
  // 何からやればいい？が消えるタスク整理術
  {
    id: 'user-guide-video-usage-tips-task-management-options',
    description:
      'label.userGuide.videoDescription.usageTips.taskManagementOptions',
    url: 'https://www.youtube.com/embed/jDShmAuA22E?si=MT7v3GHy-ljW6cDz',
  },
]

export const UserGuidePage = memo(() => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const themeMode = useAppSelector((state) => state.app.currentTheme)
  const [showApiErrors] = useShowApiErrors()

  // 画面表示時の右サイドバーの表示状態（初期表示時の値を固定）
  const rightSidebarState = useAppSelector(
    (state) => state.app.rightSideBarOpened
  )
  const [isRightSidebarVisible] = useState(rightSidebarState)

  const [loadUserSettings] = useLoadUserSettings()

  // 個人設定更新API（ユーザーガイドの既読バージョンを更新する）
  const [insertOrReplaceSettingMutation] = useInsertOrReplaceSettingMutation()

  // CrewWorksロゴ画像
  const LogoImg = useMemo(
    () => (themeMode === 'dark' ? LogoImgDark : LogoImgLight),
    [themeMode]
  )

  // 活用ガイドの画像
  const UserGuidePng = useMemo(
    () => (themeMode === 'dark' ? UserGuidePngDark : UserGuidePngLight),
    [themeMode]
  )

  // ページ最上部のref
  const pageTopRef = useRef<HTMLDivElement>(null)
  // 使い方（基本編）セクションのref
  const howToUseSectionRef = useRef<HTMLDivElement>(null)
  // 活用法（応用編）セクションのref
  const usageTipsSectionRef = useRef<HTMLDivElement>(null)
  // もっと詳しく使い方を知るセクションのref
  const learnMoreSectionRef = useRef<HTMLDivElement>(null)

  // ユーザーガイドの既読バージョンを更新する
  useEffectOnce(() => {
    const updateUserGuideReadVersion = async () => {
      try {
        // 既読バージョンを更新する
        const param: InsertOrReplaceSettingRequest = {
          userSettings: [
            {
              key: SettingKeyType.UserGuideReadVersion,
              value: String(USER_GUIDE_VERSION),
            },
          ],
        }
        await insertOrReplaceSettingMutation(param).unwrap()

        // 更新結果をreduxに反映するため、個人設定を再取得
        loadUserSettings()
      } catch (error) {
        showApiErrors(error)
      }
    }

    updateUserGuideReadVersion()
  })

  // ページ表示時に右サイドバーを閉じる
  useMount(() => {
    if (isRightSidebarVisible) {
      // 表示中の場合は閉じる
      dispatch(closeRightSideBar())
    }
  })

  // ページがアンマウントされるときに右サイドバーを元の状態に戻す
  useUnmount(() => {
    if (isRightSidebarVisible) {
      // 画面遷移前に表示していた場合は再度表示する
      dispatch(openRightSideBar())
    }
  })

  // 「使い方（基本編）」クリック時
  const handleHowToUseClick = useCallback(() => {
    // 「使い方（基本編）」セクションにスクロール
    howToUseSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  // 「活用法（応用編）」クリック時
  const handleUsageTipsClick = useCallback(() => {
    // 「活用法（応用編）」セクションにスクロール
    usageTipsSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  // 「もっと詳しい使い方を知る」クリック時
  const handleLearnMoreClick = useCallback(() => {
    // 「もっと詳しく使い方を知る」セクションにスクロール
    learnMoreSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  // 「サポートサイト」クリック時
  const handleOpenSupportSiteClick = useCallback(() => {
    // サポートサイトを別タブで開く
    const w = window.open(
      SUPPORT_SITE_HOWTO_URL,
      '_blank',
      'noopener,noreferrer'
    )
    if (w) {
      w.opener = null // セキュリティ対策として、オープナーをnullに設定
    }
  }, [])

  // 一番上までスクロールする関数
  const handleScrollToTop = useCallback(() => {
    pageTopRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [])

  return (
    <div
      ref={pageTopRef}
      className="p-2.5 @container crew-text-gray-2 relative"
    >
      <div className="flex flex-row justify-center pb-5">
        <div className="flex flex-col gap-3">
          <div
            id="user-guide-top-user-guide-content"
            className="flex flex-wrap justify-center gap-x-20 gap-y-5 py-2.5"
          >
            <div className="flex flex-col gap-y-10 pt-7">
              {/* CrewWorksロゴ */}
              <div>
                <img src={LogoImg} alt="logo" className="h-[3.75rem]" />
              </div>

              {/* 活用ガイドの画像 */}
              <div>
                <img
                  src={UserGuidePng}
                  alt={t('label.userGuide.userGuide')}
                  className="w-[30rem]"
                />
              </div>
            </div>

            {/* おすすめ活用動画 */}
            <div
              id="user-guide-top-recommended-videos"
              className="flex flex-col gap-2 items-center"
            >
              <div className="flex gap-1 items-center">
                <ArrowCircleDownOutlineRounded width={30} height={30} />
                <p className="text-3xl font-bold">
                  {t('label.userGuide.recommendedUtilizationVideos')}
                </p>
              </div>

              <div>
                {/* 「使い方(基本編)」「活用法（応用編）」の動画よりも大きく表示する */}
                <div className="w-[30rem] flex flex-col gap-2">
                  <MediaCard url={RecommendationVideoUrl.url} />
                  <p className="text-2xl font-bold">
                    {t(RecommendationVideoUrl.description)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* セクション選択タブ */}
          <div className="flex justify-center">
            <div className="w-full border border-crew-gray-3-light dark:border-crew-gray-3-dark rounded-full grid grid-cols-3">
              {/* 使い方（基本編） */}
              <button
                className="font-bold hover:bg-crew-gray-1-light hover:dark:bg-crew-gray-3-dark py-0.5 my-1 ml-3"
                onClick={handleHowToUseClick}
              >
                {t('label.userGuide.howToUse')}
              </button>
              {/* 活用法（応用編） */}
              <button
                className="font-bold hover:bg-crew-gray-1-light hover:dark:bg-crew-gray-3-dark py-0.5 my-1 border-x border-crew-gray-3-light dark:border-crew-gray-3-dark"
                onClick={handleUsageTipsClick}
              >
                {t('label.userGuide.usageTips')}
              </button>
              {/* もっと詳しく使い方を知る */}
              <button
                className="font-bold hover:bg-crew-gray-1-light hover:dark:bg-crew-gray-3-dark py-0.5 my-1 mr-3 px-12"
                onClick={handleLearnMoreClick}
              >
                {t('label.userGuide.learnMore')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 使い方（基本編） */}
      <div ref={howToUseSectionRef} className="crew-bg-gray-1 p-2.5 py-12">
        <div className="flex flex-row items-center gap-2.5 justify-center pb-5">
          <p className="text-4xl font-bold">{t('label.userGuide.howToUse')}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-8">
          {HowToUsageVideoUrls.map((video, index) => (
            // サイズを設定しておかないとflex-wrapがうまく効かない
            // w-[21rem]にしているのは、それより小さいとサムネイル表示がぼやけて表示されてしまうため。
            // おそらくYouTubeで以下の複数種類のサムネイルのサイズがあることが関係しており、
            // フレームサイズが小さいときに低解像度のサムネイルが使用されてしまうためと思われる。
            // https://portal-cms.net/sct/176
            <div
              id={video.id}
              className="flex flex-col gap-2 w-[21rem]"
              key={index}
            >
              <MediaCard url={video.url} />
              <p className="text-lg">{t(video.description)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 活用法（応用編） */}
      <div ref={usageTipsSectionRef} className="p-2.5 py-12">
        <div className="flex flex-row items-center gap-2.5 justify-center pb-5">
          <p className="text-4xl font-bold">{t('label.userGuide.usageTips')}</p>
        </div>

        <div className="flex flex-wrap justify-center gap-8">
          {UsageTipsVideoUrls.map((video, index) => (
            // サイズを設定しておかないとflex-wrapがうまく効かない
            // w-[21rem]にしているのは、それより小さいとサムネイル表示がぼやけて表示されてしまうため。
            // おそらくYouTubeで以下の複数種類のサムネイルのサイズがあることが関係しており、
            // フレームサイズが小さいときに低解像度のサムネイルが使用されてしまうためと思われる。
            // https://portal-cms.net/sct/176
            <div
              id={video.id}
              className="flex flex-col gap-2 w-[21rem]"
              key={index}
            >
              <MediaCard url={video.url} />
              <p className="text-lg">{t(video.description)}</p>
            </div>
          ))}
        </div>
      </div>

      {/* もっと詳しく使い方を知る */}
      <div
        ref={learnMoreSectionRef}
        className="crew-bg-gray-1 p-2.5 flex justify-center"
      >
        <div
          id="user-guide-learn-more"
          className="flex items-center justify-center crew-bg-default border-2 gap-8 px-14 py-6 rounded-lg max-w-4xl my-4"
        >
          <div className="space-y-6">
            <p className="text-4xl font-bold">
              {t('label.userGuide.learnMore')}
            </p>

            <div className="text-lg font-bold">
              <p>{t('label.userGuide.learnMoreDetail.line1')}</p>
              <p>{t('label.userGuide.learnMoreDetail.line2')}</p>
            </div>

            <CrewButton
              text={t('label.userGuide.openSupportSite.linkText')}
              stylingMode="outlined"
              type="normal"
              className="w-full h-11 text-lg font-bold"
              onClick={handleOpenSupportSiteClick}
            />
          </div>

          <div>
            <img
              src={LearnMorePng}
              alt={t('label.userGuide.userGuide')}
              className="w-80"
            />
          </div>
        </div>
      </div>

      <div
        id="user-guide-bottom-hide-user-guide"
        className="flex flex-col gap-2 py-10 text-lg"
      >
        <div className="flex justify-center gap-2.5">
          <IconInfo width={24} height={24} />
          <span>
            {t('label.userGuide.hideUserGuide.line1.prefix')}
            {/* 個人設定-表示タブに遷移 */}
            <CrewLink
              to={'/personal-settings/display'}
              title={t('label.userGuide.hideUserGuide.line1.linkText')}
            >
              {t('label.userGuide.hideUserGuide.line1.linkText')}
            </CrewLink>
            {t('label.userGuide.hideUserGuide.line1.suffix')}
          </span>
        </div>
        <div className="flex justify-center">
          {t('label.userGuide.hideUserGuide.line2')}
        </div>
      </div>

      {/* ページトップへ戻るボタン（右下に固定表示） */}
      <button
        onClick={handleScrollToTop}
        className={classNames(
          'sticky bottom-6 -translate-x-6 w-12 h-12 rounded-full shadow-lg flex items-center justify-center ml-auto ',
          ' bg-white dark:bg-crew-gray-1-dark hover:bg-crew-gray-1-light dark:hover:bg-crew-gray-2-dark transition-all duration-300',
          'border border-crew-gray-2-light dark:border-crew-gray-2-dark text-crew-gray-3-light dark:text-crew-gray-3-dark'
        )}
        aria-label={t('label.userGuide.backToTop')}
      >
        <ArrowUpwardAltRounded width={36} height={36} />
      </button>
    </div>
  )
})
